import { Injectable, InjectionToken, computed, inject, signal } from '@angular/core';

import { StorageKey } from '../storage/storage.keys';
import { StorageService } from '../storage/storage.service';
import {
  DEFAULT_LANGUAGE,
  isLanguage,
  type Dictionary,
  type Language,
  type TranslateParams,
} from './i18n.models';
import { EN, type TranslationKey } from './translations/en';

export const I18N_DICTIONARIES = new InjectionToken<Partial<Record<Language, Dictionary>>>(
  'I18N_DICTIONARIES',
  { providedIn: 'root', factory: () => ({ en: EN }) },
);

export const I18N_LOADERS = new InjectionToken<Record<Language, () => Promise<Dictionary>>>(
  'I18N_LOADERS',
  {
    providedIn: 'root',
    factory: () => ({
      en: () => Promise.resolve(EN),
      no: () => import('./translations/no').then((module) => module.NO),
      de: () => import('./translations/de').then((module) => module.DE),
    }),
  },
);

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly storage = inject(StorageService);

  private readonly dictionaries = signal(inject(I18N_DICTIONARIES));
  private readonly loaders = inject(I18N_LOADERS);
  private readonly pending = new Map<Language, Promise<Dictionary>>();
  private readonly initialLanguage = this.restore();
  private request = 0;
  private readonly languageSignal = signal<Language>(
    this.dictionaries()[this.initialLanguage] ? this.initialLanguage : DEFAULT_LANGUAGE,
  );

  readonly language = this.languageSignal.asReadonly();

  private readonly dictionary = computed(() => this.dictionaries()[this.languageSignal()] ?? EN);

  readonly t = (key: TranslationKey, params?: TranslateParams): string => {
    const template = this.dictionary()[key] ?? EN[key];

    return params === undefined ? template : interpolate(template, params);
  };

  /** Load the preferred dictionary before the first application render. */
  async initialize(): Promise<void> {
    try {
      await this.apply(this.initialLanguage, false);
    } catch {
      /* Keep English usable when a translation chunk cannot be downloaded. */
    }
  }

  setLanguage(language: Language): Promise<boolean> {
    return this.apply(language, true);
  }

  private async apply(language: Language, persist: boolean): Promise<boolean> {
    const request = ++this.request;
    if (!this.dictionaries()[language]) {
      let loading = this.pending.get(language);
      if (!loading) {
        loading = this.loaders[language]();
        this.pending.set(language, loading);
      }
      try {
        const dictionary = await loading;
        this.dictionaries.update((current) => ({ ...current, [language]: dictionary }));
      } finally {
        if (this.pending.get(language) === loading) this.pending.delete(language);
      }
    }
    // A slow earlier download cannot replace a newer menu/profile choice.
    if (request !== this.request) return false;
    this.languageSignal.set(language);
    if (persist) this.storage.write(StorageKey.LANGUAGE, language);
    return true;
  }

  adoptFromProfile(language: string): void {
    if (this.storage.read(StorageKey.LANGUAGE) !== null || !isLanguage(language)) {
      return;
    }

    void this.apply(language, false).catch(() => {
      // Preserve the current language if an account dictionary cannot be downloaded.
    });
  }

  private restore(): Language {
    const stored = this.storage.read(StorageKey.LANGUAGE);

    if (isLanguage(stored)) {
      return stored;
    }

    return detect() ?? DEFAULT_LANGUAGE;
  }
}

function interpolate(template: string, params: TranslateParams): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match,
  );
}

function detect(): Language | null {
  for (const tag of navigator.languages ?? []) {
    const base = tag.toLowerCase().split('-')[0];

    if (base === 'nb' || base === 'nn' || base === 'no') {
      return 'no';
    }

    if (isLanguage(base)) {
      return base;
    }
  }

  return null;
}
